import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';

interface EventRegistrationModalProps {
  eventName: string;
  onClose: () => void;
  onSubmit: (details: { name: string; email: string }) => void;
}

export const EventRegistrationModal: React.FC<EventRegistrationModalProps> = ({ eventName, onClose, onSubmit }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 300); // Matches the animation duration
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name && email) {
      onSubmit({ name, email });
      handleClose();
      // Provide a confirmation to the user
      alert(`Thank you for registering for "${eventName}", ${name}! A confirmation email will be sent to ${email}.`);
    } else {
      alert('Please fill out all fields.');
    }
  };

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleClose]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  return (
    <div
      role='dialog'
      aria-modal='true'
      aria-labelledby='modal-title'
      className='fixed inset-0 z-50 flex items-center justify-center p-4'
    >
      <div
        className={`fixed inset-0 bg-black/60 transition-opacity ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`}
        onClick={handleClose}
        aria-hidden='true'
      />

      <div
        className={`relative bg-card rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col border border-border ${
          isClosing ? 'animate-scale-out' : 'animate-scale-in'
        }`}
      >
        <div className='flex items-start justify-between p-6 border-b border-border'>
          <div>
            <h2 id='modal-title' className='text-2xl font-bold text-foreground font-heading'>
              Register for Event
            </h2>
            <p className='text-primary font-semibold'>{eventName}</p>
          </div>
          <button
            onClick={handleClose}
            aria-label='Close'
            className='text-muted-foreground hover:text-foreground transition-colors p-1 rounded-full focus:outline-none focus:ring-2 focus:ring-ring'
          >
            <X className='h-6 w-6' />
          </button>
        </div>

        <form onSubmit={handleSubmit} className='p-6 overflow-y-auto'>
          <div className='space-y-4'>
            <div>
              <label htmlFor='name' className='block text-sm font-medium text-muted-foreground mb-1'>
                Full Name
              </label>
              <input
                type='text'
                name='name'
                id='name'
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className='w-full bg-background border border-input rounded-md px-3 py-2 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-shadow'
              />
            </div>
            <div>
              <label htmlFor='email' className='block text-sm font-medium text-muted-foreground mb-1'>
                Email Address
              </label>
              <input
                type='email'
                name='email'
                id='email'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className='w-full bg-background border border-input rounded-md px-3 py-2 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-shadow'
              />
            </div>
          </div>
          <div className='pt-6'>
            <button
              type='submit'
              className='w-full bg-primary text-primary-foreground font-semibold px-6 py-3 rounded-md hover:bg-primary/90 transition-all duration-300'
            >
              Confirm Registration
            </button>
          </div>
          <p className='text-xs text-muted-foreground text-center mt-3'>
            If this event requires payment, you will be contacted with further instructions.
          </p>
        </form>
      </div>
    </div>
  );
};
